import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import Chart from "react-apexcharts";
import { FaTimes, FaInfoCircle, FaExpand } from "react-icons/fa";
import { fetchNodeAnalytics } from "../services/analyticsService";
import ExpandedChartModal from "./ExpandedChartModal";
import { getNodeTitle } from "../utils/nodeMappings";

/**
 * @file NodeDetailModal.js
 * @description Modal showing detailed performance metrics for a specific workflow node (latency, failure rates).
 */
export default function NodeDetailModal({ show, data, timeFilter, planFilter, customRange, onClose }) {
    const [analyticsData, setAnalyticsData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [expandedChart, setExpandedChart] = useState(null);

    const handleExpandChart = (title, options, series, type) => {
        setExpandedChart({ title, options, series, type });
    };

    useEffect(() => {
        if (show && data) {
            setLoading(true);
            const nodeType = data.type || data.node_type;
            fetchNodeAnalytics(nodeType, timeFilter, planFilter, customRange).then(res => {
                setAnalyticsData(res);
                setLoading(false);
            });
        } else {
            setAnalyticsData(null);
        }
    }, [show, data, timeFilter, planFilter, customRange]);

    if (!show || !data) return null;

    // Use API data if available, otherwise fallback to mock (or empty)
    const trendData = analyticsData?.latency_trend || [];

    const latencySeries = [
        { name: "Avg Latency", data: trendData.map(d => (d.avg_latency / 1000).toFixed(3)) },
        { name: "P95 Latency", data: trendData.map(d => (d.p95_latency / 1000).toFixed(3)) }
    ];

    const chartCategories = trendData.map(d => d.date);

    const latencyOptions = {
        chart: { type: "line", toolbar: { show: false }, zoom: { enabled: false } },
        stroke: { curve: "smooth", width: 2 },
        colors: ["#AD2D45", "#E68F17"],
        xaxis: {
            categories: chartCategories,
            labels: { rotate: -45, style: { fontSize: '10px' } }
        },
        yaxis: {
            title: { text: "Seconds" },
            labels: { formatter: (val) => val.toFixed(3) }
        },
        grid: { borderColor: "#f1f5f9", strokeDashArray: 4 },
        tooltip: {
            y: { formatter: (val) => `${val}s` }
        }
    };

    return (
        <Modal show={show} onHide={onClose} size="lg" centered contentClassName="border-0 shadow-lg">
            <div className="p-4 bg-white rounded-3">
                {/* HEADER */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <div>
                        <h4 className="fw-bold mb-0">{getNodeTitle(data.node_name || data.name)}</h4>
                        <span className="badge bg-primary-subtle text-primary mt-2">{getNodeTitle(data.type || data.node_type)}</span>
                    </div>
                    <button className="btn btn-close" onClick={onClose}></button>
                </div>

                {loading ? (
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status"></div>
                        <p className="mt-2 text-muted">Loading analytics...</p>
                    </div>
                ) : (
                    <>
                        {/* METRICS SUMMARY */}
                        {analyticsData && (
                            <div className="row g-3 mb-3">
                                <div className="col-6">
                                    <div className="p-3 bg-light rounded text-center">
                                        <div className="text-muted small fw-bold">Total Executions Analyzed</div>
                                        <div className="h5 fw-bold mb-0">{analyticsData.total_executions_analyzed}</div>
                                    </div>
                                </div>
                                <div className="col-6">
                                    <div className="p-3 bg-light rounded text-center">
                                        <div className="text-muted small fw-bold">Node Instances</div>
                                        <div className="h5 fw-bold mb-0">{analyticsData.node_instances_found}</div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* CHART SECTION */}
                        <div className="card border shadow-sm p-4 mb-4 rounded-4">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <div className="d-flex align-items-center gap-2">
                                    <h6 className="fw-bold mb-0">Latency Trend</h6>
                                    <FaInfoCircle className="text-primary small" size={12} />
                                </div>
                                <button className="btn btn-sm btn-link text-secondary p-0" onClick={() => handleExpandChart("Latency Trend", latencyOptions, latencySeries, "line")}>
                                    <FaExpand />
                                </button>
                            </div>
                            <div style={{ minHeight: "250px" }}>
                                {trendData.length > 0 ? (
                                    <Chart options={latencyOptions} series={latencySeries} type="line" height={250} />
                                ) : (
                                    <div className="text-center text-muted py-5">No latency data available for this period.</div>
                                )}
                            </div>
                        </div>
                    </>
                )}
            </div>
            <ExpandedChartModal
                show={!!expandedChart}
                onClose={() => setExpandedChart(null)}
                {...expandedChart}
            />
        </Modal>
    );
}
